import { PlusIcon } from "@xola/icons";
import { ButtonGroup, Label } from "@xola/ui-kit";
import { now } from "common";
import React, { useEffect, useState } from "react";
import { BaseSchedule, BaseScheduleRepeat, BaseScheduleType, Schedule } from "types";
import { useVisibility } from "../../hooks/useVisibility";
import { isActive } from "../../models/Schedule";
import { getAllWeekdays } from "../../models/Weekday";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ScheduleEditorModal } from "../Product/SchedulesAvailability/ScheduleEditorModal";
import { BaseScheduleButtonGroup } from "./BaseScheduleButtonGroup";
import { BaseScheduleSummary } from "./BaseScheduleSummary";

export enum BaseScheduleFilterType {
    ACTIVE = "active",
    EXPIRED = "expired",
    ALL = "all",
}

enum ButtonType {
    ACTIVE = 0,
    EXPIRED = 1,
    ALL = 2,
}
enum ScheduleEditModalTypes {
    EDIT = "edit",
    CREATE = "create",
}

export const defaultValues: BaseSchedule = {
    name: "",
    type: BaseScheduleType.AVAILABLE,
    start: now(),
    end: null,
    days: getAllWeekdays(),
    timeRanges: [],
    repeat: BaseScheduleRepeat.WEEKLY,
};

interface ScheduleInputProps {
    schedules: BaseSchedule[];
    onUpsertSchedule: (schedule: BaseSchedule) => void;
    onDeleteSchedule: (schedule: BaseSchedule) => void;
    description?: string;
    errorMessage?: string | null;
    errorScheduleUpdateType?: ScheduleEditModalTypes.CREATE | ScheduleEditModalTypes.EDIT | null;
}

const scheduleFilters = [BaseScheduleFilterType.ACTIVE, BaseScheduleFilterType.EXPIRED, BaseScheduleFilterType.ALL];

export const BaseScheduleInput = ({
    schedules = [],
    onUpsertSchedule,
    onDeleteSchedule,
    description,
    errorMessage,
    errorScheduleUpdateType,
}: ScheduleInputProps) => {
    const [editedSchedule, setEditedSchedule] = useState<BaseSchedule | null>(null);
    const [activeButton, setActiveButton] = useState(ButtonType.ALL);
    const [filter, setFilter] = useState(BaseScheduleFilterType.ALL);
    // isApplyUnavailable used to prevent request sending in ScheduleEditorModal if user doesn`t have correct permission rights
    const [isApplyUnavailable, setIsApplyUnavailable] = useState(false);
    const [scheduleEditorModalErrorHandlerType, setScheduleEditorModalErrorHandlerType] = useState<
        ScheduleEditModalTypes.EDIT | ScheduleEditModalTypes.CREATE | null
    >(null);

    const [isScheduleEditorOpen, { show: showSheduleEditor, hide: hideScheduleEditor }] = useVisibility(false);

    const schedulesForFilter = schedules.filter((schedule) => {
        return filter === BaseScheduleFilterType.ACTIVE ? isActive(schedule) : !isActive(schedule);
    });

    const filteredSchedules = filter === BaseScheduleFilterType.ALL ? schedules : schedulesForFilter;

    const handleAddSchedule = () => {
        setEditedSchedule(defaultValues);
        handleScheduleEditorOpen(ScheduleEditModalTypes.CREATE);
        showSheduleEditor();
    };

    const handleEditButton = (schedule: BaseSchedule) => {
        setEditedSchedule({ ...defaultValues, ...schedule });
        setScheduleEditorModalErrorHandlerType(ScheduleEditModalTypes.EDIT);
        showSheduleEditor();
    };

    const handlePaymentStatusChange = (activeIndex: number) => {
        setFilter(scheduleFilters[activeIndex]);
        setActiveButton(activeIndex);
    };

    const handleScheduleEditorOpen = (
        modalType: ScheduleEditModalTypes.CREATE | ScheduleEditModalTypes.EDIT | null,
    ) => {
        setScheduleEditorModalErrorHandlerType(modalType);
    };

    // This logic needed to prevent send request on Schedule Editor Apply, if user open the same modal after
    // he got Permission api error for creating/editing
    // But there is case if user got permission error in create modal, he still should be able to apply edit modal.
    // To handle this used scheduleEditorModalErrorHandlerType and errorScheduleUpdateType to check if error was got in the same modal
    // that user trying to open again.
    // errorMessage not null only after permission api error
    useEffect(() => {
        if (errorMessage && scheduleEditorModalErrorHandlerType === errorScheduleUpdateType) {
            setIsApplyUnavailable(true);
            return;
        }

        setIsApplyUnavailable(false);
    }, [scheduleEditorModalErrorHandlerType, errorScheduleUpdateType, errorMessage]);

    return (
        <>
            <Label className="!m-0 flex items-center justify-between">
                <h4 className="font-bold">Schedules</h4>
                <div>
                    <ButtonGroup className="mr-2 align-top" value={activeButton} onChange={handlePaymentStatusChange}>
                        <ButtonGroup.Button>Active</ButtonGroup.Button>
                        <ButtonGroup.Button>Expired</ButtonGroup.Button>
                        <ButtonGroup.Button>All</ButtonGroup.Button>
                    </ButtonGroup>

                    <SecondaryButton icon={<PlusIcon size="small" />} onClick={handleAddSchedule}>
                        Add Schedule
                    </SecondaryButton>
                </div>
            </Label>
            {filteredSchedules?.map((schedule) => {
                return (
                    <div
                        key={schedule.id}
                        className="mt-2 flex justify-between rounded border border-gray-light p-4 hover:bg-gray-lighter"
                    >
                        <BaseScheduleSummary showDetails schedule={schedule}>
                            <BaseScheduleButtonGroup
                                schedule={schedule}
                                handleEditButton={handleEditButton}
                                onDeleteSchedule={onDeleteSchedule}
                            />
                        </BaseScheduleSummary>
                    </div>
                );
            })}

            {schedules?.length === 0 && (
                <div data-testid={`base-schedule-input-empty-${BaseScheduleType.AVAILABLE}-schedule`}>No times</div>
            )}

            <ScheduleEditorModal
                disablePastDays
                isNotResourceSchedule={false}
                title="Schedule Settings"
                description={description}
                isApplyUnavailable={isApplyUnavailable}
                errorMessage={errorMessage}
                isOpen={isScheduleEditorOpen}
                initialValues={editedSchedule as Schedule}
                onApply={(schedule) => {
                    onUpsertSchedule(schedule);
                }}
                onClose={() => {
                    hideScheduleEditor();
                }}
            />
        </>
    );
};
